import { useMemo } from 'react';
import { Calendar, Plus } from 'lucide-react';
import clsx from 'clsx';
import type { ShiftInstance } from '../../services/api';
import type { ScheduleEmployee } from '../../hooks/useScheduleBuilder';
import { parseLocalDate } from '../../hooks/useScheduleBuilder';
import ShiftSlot from './ShiftSlot';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface ScheduleBuilderGridProps {
  selectedWeek: { start: string; end: string };
  shiftsByDate: Record<string, ShiftInstance[]>;
  employees: ScheduleEmployee[];
  getStaffingSummary: (shift: ShiftInstance) => Map<string, { required: number; assigned: number }>;
  onRemoveAssignment: (shiftId: string, assignmentId: string) => void;
  onShiftClick?: (shift: ShiftInstance) => void;
  onAddShift?: (date: string) => void;
  highlightEmployeeId?: string | null;
  onQuickAssign?: (shiftId: string, employeeId: string, role: string) => Promise<void>;
}

export default function ScheduleBuilderGrid({
  selectedWeek,
  shiftsByDate,
  employees,
  getStaffingSummary,
  onRemoveAssignment,
  onShiftClick,
  onAddShift,
  highlightEmployeeId,
  onQuickAssign,
}: ScheduleBuilderGridProps) {
  // Generate array of dates for the week
  const weekDates = useMemo(() => {
    const dates: string[] = [];
    const start = parseLocalDate(selectedWeek.start);

    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }

    return dates;
  }, [selectedWeek.start]);

  // Check if date is today
  const isToday = (dateStr: string) => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    return dateStr === todayStr;
  };

  // Check if date is in the past
  const isPast = (dateStr: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = parseLocalDate(dateStr);
    return date < today;
  };

  return (
    <div className="flex-1 overflow-auto">
      {/* Mobile: Horizontal swipe layout */}
      <div className="md:hidden flex flex-col h-full">
        {/* Day pills - tap to jump */}
        <div className="flex gap-1 px-3 pb-2 overflow-x-auto scrollbar-hide">
          {weekDates.map((dateStr) => {
            const date = parseLocalDate(dateStr);
            const dayOfWeek = date.getDay();
            const dayNum = date.getDate();
            const today = isToday(dateStr);
            const past = isPast(dateStr);

            return (
              <button
                key={dateStr}
                onClick={() => {
                  const el = document.getElementById(`day-${dateStr}`);
                  el?.scrollIntoView({ behavior: 'smooth', inline: 'center' });
                }}
                className={clsx(
                  'flex-shrink-0 px-3 py-1.5 rounded-full text-center transition-colors',
                  today
                    ? 'bg-indigo-500 text-white'
                    : past
                    ? 'bg-slate-100 text-slate-400'
                    : 'bg-slate-100 text-slate-600 active:bg-slate-200'
                )}
              >
                <div className="text-[10px] font-medium uppercase">{DAY_NAMES[dayOfWeek]}</div>
                <div className="text-sm font-semibold -mt-0.5">{dayNum}</div>
              </button>
            );
          })}
        </div>

        {/* Swipeable day cards */}
        <div className="flex-1 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
          <div className="flex h-full">
            {weekDates.map((dateStr) => {
              const shifts = shiftsByDate[dateStr] || [];
              const past = isPast(dateStr);
              const today = isToday(dateStr);
              const date = parseLocalDate(dateStr);
              const dayOfWeek = date.getDay();

              return (
                <div
                  key={dateStr}
                  id={`day-${dateStr}`}
                  className="w-full flex-shrink-0 snap-center px-3 pb-3"
                >
                  <div
                    className={clsx(
                      'h-full rounded-2xl p-4 space-y-3 overflow-y-auto',
                      today
                        ? 'bg-indigo-50/80 border border-indigo-100'
                        : 'bg-white border border-slate-100',
                      past && !today && 'opacity-60'
                    )}
                  >
                    {/* Day header */}
                    <div className="text-center pb-2 border-b border-slate-100">
                      <div className="text-lg font-semibold text-slate-800">
                        {DAY_NAMES_FULL[dayOfWeek]}
                      </div>
                      {today && (
                        <div className="text-xs text-indigo-500 font-medium">Today</div>
                      )}
                    </div>

                    {/* Shifts */}
                    {shifts.length > 0 ? (
                      <div className="space-y-3">
                        {shifts.map((shift) => {
                          const hasHighlightedEmployee = highlightEmployeeId
                            ? (shift.assignments || []).some(a => a.employeeId === highlightEmployeeId)
                            : false;
                          const isDimmed = !!(highlightEmployeeId && !hasHighlightedEmployee);
                          const assignedIds = new Set((shift.assignments || []).map(a => a.employeeId));
                          const availableForShift = employees.filter(emp => !assignedIds.has(emp.id));

                          return (
                            <ShiftSlot
                              key={shift.id}
                              shift={shift}
                              staffingSummary={getStaffingSummary(shift)}
                              onRemoveAssignment={(assignmentId) =>
                                onRemoveAssignment(shift.id, assignmentId)
                              }
                              onShiftClick={() => onShiftClick?.(shift)}
                              employees={employees}
                              isHighlighted={hasHighlightedEmployee}
                              isDimmed={isDimmed}
                              highlightEmployeeId={highlightEmployeeId}
                              onQuickAssign={onQuickAssign ? (empId, role) => onQuickAssign(shift.id, empId, role) : undefined}
                              availableEmployees={availableForShift}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex-1 flex flex-col items-center justify-center py-12 text-slate-300">
                        <Calendar className="w-10 h-10 opacity-30" />
                        <p className="text-sm mt-2">No shifts scheduled</p>
                      </div>
                    )}

                    {/* Add shift button */}
                    {!past && onAddShift && (
                      <button
                        onClick={() => onAddShift(dateStr)}
                        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-slate-400 active:text-indigo-600 active:bg-indigo-50 rounded-xl border-2 border-dashed border-slate-200 active:border-indigo-300 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                        <span>Add Shift</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Desktop: Original 7-column grid */}
      <div className="hidden md:block overflow-auto max-h-full">
        <div className="min-w-[800px]">
          {/* Header row with day names */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDates.map((dateStr) => {
              const date = parseLocalDate(dateStr);
              const dayOfWeek = date.getDay();
              const dayNum = date.getDate();
              const today = isToday(dateStr);
              const past = isPast(dateStr);

              return (
                <div
                  key={dateStr}
                  className={clsx(
                    'text-center py-2 rounded-lg',
                    today && 'bg-indigo-100',
                    past && !today && 'opacity-50'
                  )}
                >
                  <div
                    className={clsx(
                      'text-xs font-medium uppercase tracking-wide',
                      today ? 'text-indigo-600' : 'text-slate-400'
                    )}
                  >
                    {DAY_NAMES[dayOfWeek]}
                  </div>
                  <div
                    className={clsx(
                      'text-lg font-semibold',
                      today ? 'text-indigo-600' : 'text-slate-700'
                    )}
                  >
                    {dayNum}
                  </div>
                  {today && (
                    <div className="text-xs text-indigo-500 font-medium">Today</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Grid with shifts */}
          <div className="grid grid-cols-7 gap-2">
            {weekDates.map((dateStr) => {
              const shifts = shiftsByDate[dateStr] || [];
              const past = isPast(dateStr);
              const today = isToday(dateStr);

              return (
                <div
                  key={dateStr}
                  className={clsx(
                    'min-h-[200px] rounded-xl p-2 space-y-2',
                    today ? 'bg-indigo-50/50 border border-indigo-100' : 'bg-slate-50/80 border border-slate-100',
                    past && !today && 'opacity-50'
                  )}
                >
                  {shifts.length > 0 ? (
                    shifts.map((shift) => {
                      const hasHighlightedEmployee = highlightEmployeeId
                        ? (shift.assignments || []).some(a => a.employeeId === highlightEmployeeId)
                        : false;
                      const isDimmed = !!(highlightEmployeeId && !hasHighlightedEmployee);
                      const assignedIds = new Set((shift.assignments || []).map(a => a.employeeId));
                      const availableForShift = employees.filter(emp => !assignedIds.has(emp.id));

                      return (
                        <ShiftSlot
                          key={shift.id}
                          shift={shift}
                          staffingSummary={getStaffingSummary(shift)}
                          onRemoveAssignment={(assignmentId) =>
                            onRemoveAssignment(shift.id, assignmentId)
                          }
                          onShiftClick={() => onShiftClick?.(shift)}
                          employees={employees}
                          isHighlighted={hasHighlightedEmployee}
                          isDimmed={isDimmed}
                          highlightEmployeeId={highlightEmployeeId}
                          onQuickAssign={onQuickAssign ? (empId, role) => onQuickAssign(shift.id, empId, role) : undefined}
                          availableEmployees={availableForShift}
                        />
                      );
                    })
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <Calendar className="w-5 h-5 opacity-40" />
                      <p className="text-[10px] mt-1">No shifts</p>
                    </div>
                  )}

                  {/* Add shift button */}
                  {!past && onAddShift && (
                    <button
                      onClick={() => onAddShift(dateStr)}
                      className="w-full flex items-center justify-center gap-1 py-2 text-xs font-medium text-slate-400 hover:text-indigo-600 active:bg-indigo-50 rounded-lg border border-dashed border-slate-200 hover:border-indigo-300 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add</span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
